import axios from 'axios';

const FEES_API_URL = `${process.env.REACT_APP_API_URL}/fees`;

// Order interfaces
export interface Order {
  id?: number;
  orderId?: string;
  entity?: string;
  amount?: number;
  amountPaid?: number;
  amountDue?: number;
  currency?: string;
  receipt?: string;
  status?: string;
  studentName?: string;
  studentCode?: string;
  createdAt?: string | number[]; // LocalDateTime from Java can be array format [year, month, day, hour, minute, second]
  classId?: number;
  sessionId?: number;
}

export interface OrderRequestDto {
  amount: number;
  studentCode: string;
  studentName: string;
  classId: number;
  sessionId: number;
}

export interface CreateOrderRequest {
  amount: number;
}

export interface CreateOrderResponse {
  id?: string;
  orderId?: string;
  amount?: number;
  currency?: string;
  receipt?: string;
  status?: string;
  [key: string]: any; // Allow for additional fields from backend
}

// Fee Receipt interfaces
export interface FeeReceipt {
  id: number;
  orderId?: string;
  paymentId?: string;
  studentCode?: string;
  studentName?: string;
  sessionId?: number;
  classId?: number;
  amount?: number;
  status?: string;
  localDateTime: string | number[];
  feeTransaction?: {
    id: number;
    transactionId: string;
    studentAccount: {
      id: number;
      studentAccountNumber: string;
      dueAmount?: number;
      student: {
        id: number;
        studentCode: string;
        studentName: string;
        scholarNo?: string;
        fatherName?: string;
        motherName?: string;
        sessionName?: string;
        className?: string;
        session?: {
          id: number;
          name: string;
        };
        schoolClass?: {
          id: number;
          name: string;
          section?: string;
        };
        [key: string]: any;
      }
    };
    transactionType: string;
    paymentType: string;
    amount: number;
    session?: {
      id: number;
      name: string;
    };
    schoolClass?: {
      id: number;
      name: string;
      section?: string;
    };
    createdAt?: string | number[];
    [key: string]: any;
  };
  particulars?: any[];
}

export interface PaymentVerificationDto {
  paymentId: string;
  orderId: string;
  amount: number;
  signature: string;
  studentCode: string;
  studentName: string;
  classId: number;
  sessionId: number;
}

/**
 * Pay fee - Create a payment order
 * This function internally calls createOrder
 * @param orderRequest - The order request containing amount, studentCode, studentName, classId, and sessionId
 * @returns Promise with order response data
 */
export const payFee = async (orderRequest: OrderRequestDto): Promise<CreateOrderResponse> => {
  try {
    // Call the createOrder function internally
    const orderResponse = await createOrder(orderRequest);

    // Transform the Order response to CreateOrderResponse format
    const response: CreateOrderResponse = {
      ...orderResponse, // Include all fields from order response
      id: orderResponse.id?.toString(),
      orderId: orderResponse.orderId || orderResponse.id?.toString(),
      amount: orderResponse.amount,
      currency: orderResponse.currency,
      receipt: orderResponse.receipt,
      status: orderResponse.status,
    };

    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Create a payment order using /fees/order endpoint
 * @param orderRequest - The order request containing amount, studentCode, studentName, classId, and sessionId
 * @returns Promise with order response data
 */
export const createOrder = async (orderRequest: OrderRequestDto): Promise<Order> => {
  try {
    const response = await axios.post<Order>(`${FEES_API_URL}/order`, orderRequest, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Verify payment
 * @param paymentData - Payment verification data
 * @returns Promise with fee receipt data
 */
export const verifyPayment = async (paymentData: PaymentVerificationDto): Promise<FeeReceipt> => {
  const response = await axios.post<FeeReceipt>(`${FEES_API_URL}/verify-fees`, paymentData);
  return response.data;
};

